import React, { useContext, useMemo } from 'react';
import { flowRight } from 'lodash';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import RowLabelTooltip from 'components/RowLabelTooltip/RowLabelTooltip';
import AlternateCurrencyChip from 'components/ScalarSpreadsheet/utilities/alternate-currency-chip/AlternateCurrencyChip';
import withChart from 'components/WithChart/withChart';
import withTooltip from 'components/WithTooltip/withTooltip';
import { useRevealEllipsisInTooltip } from 'services/hooks';
import { getCellViewerDisplayValue, getCellViewerFormat, getCellViewerSpanId } from './getCellViewerDisplayValue';

interface DisplayValueViewerProps {
  cell: any;
  selected?: boolean;
}

const DisplayValueViewer: React.FC<DisplayValueViewerProps> = ({ cell }) => {
  const { tooltipRef, width } = useRevealEllipsisInTooltip();
  const { format } = useContext(FeaturedSpreadsheetContext);
  const cellFormat = useMemo(() => getCellViewerFormat(cell, format), [cell, format]);
  const value = useMemo(() => getCellViewerDisplayValue(cell, cellFormat), [cell, cellFormat]);
  const spanId = useMemo(() => getCellViewerSpanId(cell), [cell]);
  const valueOrNA = cell.value !== null ? value : 'N/A';

  if (
    cell.className?.includes('table-header') ||
    cell.className?.includes('header-no-border') ||
    cell.className?.includes('show-tooltip')
  ) {
    return (
      <span className="value-viewer" ref={tooltipRef}>
        {value ? (
          <RowLabelTooltip title={value} containerWidth={width}>
            <span id={`${cell.alias}-${cell.key}`} className="text-cell">
              {value}
            </span>
          </RowLabelTooltip>
        ) : (
          <span id={`${cell.alias}-${cell.key}`} className="text-cell">
            {value}
          </span>
        )}
      </span>
    );
  }

  if (cell.currencySpecification && format.currency.additionalCurrencies) {
    return (
      <AlternateCurrencyChip
        value={!cell.displayNAforNull ? value : valueOrNA}
        exchangeRate={cellFormat.currency.exchangeRate}
        spanId={spanId}
        className={cell.className}
        baseCurrency={format.currency.code as string}
        sourceCurrency={cellFormat.currency.sourceCurrency}
      />
    );
  }

  return (
    <span className="value-viewer">
      <span id={spanId} className={`text-cell ${cell.className ?? ''}`}>
        {!cell.displayNAforNull ? value : valueOrNA}
      </span>
    </span>
  );
};

const enhance = flowRight(withTooltip, withChart);

export default enhance(DisplayValueViewer);
